import { Fragment, useEffect, useState, useRef } from 'react'
import { Dialog, DialogPanel, Transition } from '@headlessui/react'
import DailyIframe from '@daily-co/daily-js'

interface VideoCallModalProps {
  open: boolean
  onClose: () => void
  roomUrl: string
  userName: string
  isHost?: boolean
}

export default function VideoCallModal({ open, onClose, roomUrl, userName }: VideoCallModalProps) {
  const [callObject, setCallObject] = useState<any>(null)
  const [containerElement, setContainerElement] = useState<HTMLDivElement | null>(null)
  const creatingRef = useRef(false)

  // Create Daily frame when container is available
  useEffect(() => {
    if (containerElement && !callObject && open && !creatingRef.current) {
      creatingRef.current = true

      try {
        // Check if there's an existing Daily instance and destroy it first
        const existingInstances = DailyIframe.getCallInstance()
        if (existingInstances) {
          console.log('Found existing Daily instance, destroying it first...')
          existingInstances.destroy()
        }

        const daily = DailyIframe.createFrame(containerElement, {
          showLeaveButton: true,
          showFullscreenButton: true,
          iframeStyle: {
            width: '100%',
            height: '100%',
            border: 'none',
            borderRadius: '0.75rem'
          }
        })
        setCallObject(daily)
      } catch (error) {
        console.error('Error creating Daily frame:', error)
        creatingRef.current = false
      }
    }
  }, [containerElement, callObject, open])

  // Join meeting when call object is ready
  useEffect(() => {
    if (callObject && roomUrl && open) {
      callObject.join({
        url: roomUrl,
        userName: userName
      }).then(() => {
      }).catch((error: any) => {
        console.error('Failed to join meeting:', error)
      })
    }
  }, [callObject, roomUrl, userName, open])

  // Cleanup when modal closes
  useEffect(() => {
    if (!open && callObject) {
      callObject.destroy()
      setCallObject(null)
      creatingRef.current = false
    }
  }, [open, callObject])

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[140]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/75" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-6xl transform overflow-hidden rounded-2xl bg-gray-900 shadow-xl transition-all h-[80vh]">
                <div ref={setContainerElement} className="w-full h-full" />
              </DialogPanel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
